import { Typography } from '@ori-ui/mui';
import type {
  GlobalCustomerRegistrationsRegistrationLegalDetailsDto,
  GlobalCustomersRequiredTermDto,
} from '@ori/global-api-client-react';
import { getCustomerRequiredTerms, useApiClient } from '@ori/global-api-client-react';
import type { StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Localization, ContentKeys } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { GetContent, useConfiguration, useNotificationData } from '../../contexts';
import { useCustomHeaders } from '../../hooks';
import { MessageType } from '../../interfaces';
import { FetchResult, TranslationKey } from '../../models';
import { useRegistrationData } from '../../models/api';
import { ExpandableTextBlock } from '../ExpandableTextBlock';
import { CheckboxFormFieldByConfig } from '../FieldsByConfig';
import { StyledLink, PolicyText } from '../Forms/LegalForm/StyledComponentsLegalForm';
import { Loading } from '../Loading';

interface TermsConditionsCheckboxProps {
  config?: StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto;
}

export const TermsConditionsCheckbox: FC<TermsConditionsCheckboxProps> = (props) => {
  const { config } = props;

  const notificationStore = useNotificationData().context.notificationData;

  const { tenant } = useConfiguration();
  const headers = useCustomHeaders();

  const api = useApiClient();

  const { getTestId } = useTestId();

  const [terms, setTerms] = useState<GlobalCustomersRequiredTermDto>();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  const { data: registrationData } = useRegistrationData();

  const form = useFormContext<GlobalCustomerRegistrationsRegistrationLegalDetailsDto>();

  const { watch, setValue, trigger } = form;

  const isCustomTextAvailable = config?.customText?.isEnabled;

  const getTerms = useCallback(async () => {
    try {
      if (registrationData?.customerId) {
        const termsResponse = await getCustomerRequiredTerms(api, registrationData.customerId, {}, headers);
        const result = new FetchResult<unknown>(termsResponse.status);
        if (result.isSuccess() && termsResponse.data.collection.length > 0) {
          const isShowPrivacyPolicy = termsResponse.data.collection.some(
            (requiredTerm) => requiredTerm.type === 'PrivacyPolicy',
          );
          setShowPrivacyPolicy(isShowPrivacyPolicy);
          setTerms(termsResponse.data.collection[0]);
        }
      }
    } catch (error: unknown) {
      notificationStore.setNotificationData({
        isOpen: true,
        message: (
          <Localization
            id={TranslationKey.cannotUpdate}
            defaultMessage={defaultTranslations.cannotUpdate}
          />
        ),
        type: MessageType.error,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --- not needed updated on other property
  }, [tenant, registrationData]);

  useEffect(() => {
    if (config?.termsAndConditions?.isAvailable) {
      getTerms();
    }
  }, [config, registrationData, getTerms]);

  const values = watch();

  const termId = values.termsAndConditions?.termId;

  if (config?.termsAndConditions?.isAvailable && !terms) {
    return <Loading />;
  }

  return (
    <div style={{ order: config?.termsAndConditions?.displayOrder }}>
      <CheckboxFormFieldByConfig
        name="termsAndConditions"
        label={
          <Typography
            variant="body1"
            sx={{ fontWeight: termId ? 'bold' : 'normal' }}
          >
            <Localization
              id={TranslationKey.TermsAndConditions}
              defaultMessage={defaultTranslations.TermsAndConditions}
              values={{
                TermsAndConditions_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.TermsAndConditions_Url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.TermsAndConditions_Label}
                      defaultMessage={defaultTranslations.TermsAndConditions_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument1_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument1_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument1_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument1_Link_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument2_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument2_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument2_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument2_Link_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument3_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument3_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument3_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument3_Link_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument4_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument4_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument4_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument4_Link_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument5_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument5_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument5_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument5_Link_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument6_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument6_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument6_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument6_Link_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument7_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument7_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument7_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument7_Link_Label}
                    />
                  </StyledLink>
                ),
                LegalDocument8_Link: (
                  <StyledLink
                    href={GetContent(ContentKeys.LegalDocument8_Link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Localization
                      id={TranslationKey.LegalDocument8_Link_Label}
                      defaultMessage={defaultTranslations.LegalDocument8_Link_Label}
                    />
                  </StyledLink>
                ),
              }}
            />
            *
          </Typography>
        }
        value={!!termId}
        onChange={(_e, value) => {
          if (value) {
            setValue(
              'termsAndConditions',
              { termId: terms?.termId },
              { shouldDirty: true, shouldTouch: true, shouldValidate: true },
            );
            trigger('termsAndConditions');
          } else {
            setValue('termsAndConditions', undefined, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            trigger('termsAndConditions');
          }
        }}
        {...config?.termsAndConditions}
      />
      {showPrivacyPolicy ? (
        <PolicyText
          variant="body1"
          textAlign="left"
        >
          <Localization
            id={TranslationKey.PrivacyPolicy_Text}
            defaultMessage={defaultTranslations.PrivacyPolicy_Text}
            values={{
              PrivacyPolicy_Link: (
                <StyledLink
                  href={GetContent(ContentKeys.PrivacyPolicy_Url)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Localization
                    id={TranslationKey.PrivacyPolicy_Label}
                    defaultMessage={defaultTranslations.PrivacyPolicy_Label}
                  />
                </StyledLink>
              ),
            }}
          />
        </PolicyText>
      ) : null}
      {isCustomTextAvailable ? (
        <ExpandableTextBlock dataTestId={getTestId('custom-text-btn-more')}>
          <Localization
            id={TranslationKey.Custom_text}
            defaultMessage={defaultTranslations.Custom_text}
          />
        </ExpandableTextBlock>
      ) : null}
    </div>
  );
};
